import { DISCOUNT_TYPES } from './promo-code.interface';
import { PromoCodeApplyResponse } from '../../../../services/promocode/promo-code-api.interface';
import { Session } from '../../dto/charger.dto';

/**
 * Normalizes the API response to handle both nested and flat response structures.
 * The backend may return discount data either wrapped in `appliedDiscount` or at the top level.
 */
export const normalizeApplyPromoCodeResponse = (result: PromoCodeApplyResponse) => {
  return (
    result.appliedDiscount ??
    (result.promoCode
      ? {
          promoCode: result.promoCode,
          discountType: result.discountType,
          discountValue: result.discountValue,
        }
      : undefined)
  );
};

/**
 * Formats a discount value for display based on its type.
 * @param discountType - Either 'PERCENTAGE' or 'FIXED_AMOUNT'
 * @param discountValue - The numeric discount value
 * @param t - Translation function for the 'discount_off_label'
 */
export const formatDiscountValue = (
  discountType: string,
  discountValue: number,
  t: (key: string) => string,
): string => {
  if (discountType === DISCOUNT_TYPES.PERCENTAGE) {
    return `${discountValue}% ${t('discount_off_label')}`;
  }
  return `$${discountValue} ${t('discount_off_label')}`;
};

/**
 * Formats the discount percentage display string.
 * Shows "Free" for 100% discounts, otherwise shows the percentage.
 * @param percentage - The discount percentage (0-100)
 * @param t - Translation function
 */
export const formatDiscountPercentageLabel = (
  percentage: number,
  t: (key: string) => string,
): string => {
  return percentage === 100 ? t('free') : `${percentage}%`;
};

/**
 * Extracts applied promo code details from an active session's running totals.
 * Returns null if no discount is applied.
 */
export const getAppliedPromoFromSession = (session: Session | null | undefined) => {
  const firstDiscount = session?.runningTotals?.discounts?.[0];
  if (!firstDiscount?.discount?.promoCode) {
    return null;
  }

  return {
    code: firstDiscount.discount.promoCode,
    discountType:
      firstDiscount.discount.method === 'PERCENTAGE'
        ? ('PERCENTAGE' as const)
        : ('FIXED_AMOUNT' as const),
    discountValue: firstDiscount.discount.value,
  };
};

export const PROMO_CODE_ERRORS = {
  API_ERROR: 'API_ERROR',
} as const;
